/* eslint-disable react/prop-types */

import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getReportData, getTotalCount } from '../../../../../api/reports';
import CommonPagination from '../../../../../common/components/pagination/pagination';
import { useSetLoading } from '../../../../../common/hooks/appearance';
import { usePagination } from '../../../../../common/hooks/data';
import { detailedContractHeader, downloadTypesEmail } from '../../../../../utils/constant';
import { apiErrorHandler, successHandler } from '../../../../../utils/handlers';
import DownloadModal from '../../../../reports/downloadModal/downloadModal';
import EmailModal from '../../../../reports/emailModal/emailModal';
import ItemEmptyTile from '../../../../reports/item-empty-tile/item-empty-tile';
import Toolbar from '../../../../reports/toolbar/toolbar';
import CommonComponentForTableData from './commonComponentForTableData';

export default function TrustDetailedReportData({ showTable, setShowTable, title, subtitle }) {
  const { page, limit, setPage, setLimit } = usePagination();
  const [loading, setLoading] = useState(false);
  const [slStartNumber, setSlStartNumber] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  const toggleDownloadModal = () => {
    if (showTable?.tableData?.length) {
      setIsDownloadModalOpen(!isDownloadModalOpen);
    }
  };
  const toggleEmailModal = () => {
    if (showTable?.tableData?.length) {
      setIsEmailModalOpen(!isEmailModalOpen);
    }
  };

  const onSerchInputChange = (event) => {
    setPage(1);
    setLimit(5);

    setSearchText(event.target.value.trim());
    debounceApiCall(event.target.value, 1);
  };

  const getUpdatedTableData = async (searchVal, pageValue = null) => {
    const requestParams = {
      startDate: showTable.fromDate,
      endDate: showTable.toDate,
      trustType: showTable?.entity?.toString() !== '' ? showTable?.entity?.toString() : undefined,
      needType:
        showTable?.agreementTypes?.toString() !== ''
          ? showTable?.agreementTypes?.toString()
          : undefined,
      businessUnit:
        showTable?.businessUnits !== '' ? showTable?.businessUnits?.toString() : undefined,
      timezone: moment.tz.guess(),
      search: searchVal,
      page: pageValue || page,
      limit: 5,
    };
    if (showTable?.trustType !== 0) requestParams.trustType = showTable.trustType;
    if (showTable?.needType !== 0) requestParams.needType = showTable.needType;
    fetchTrustDetailedReport(requestParams);
    if ((pageValue || page) === 1) fetchTotalCount(requestParams);
  };

  const fetchTrustDetailedReport = async (requestParams) => {
    setLoading(true);
    try {
      const response = await getReportData('trustDetailedReport', requestParams);
      setShowTable((value) => {
        return {
          ...value,
          show: true,
          isSearchResult: true,
          tableData: response?.data?.data?.result || [],
          page: response?.data?.data?.page || 0,
          limit: response?.data?.data?.limit || 0,
        };
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      apiErrorHandler(error);
    }
  };

  const fetchTotalCount = async (requestParams) => {
    setShowTable((value) => {
      return {
        ...value,
        totalResults: 'Loading...',
      };
    });
    try {
      const response = await getTotalCount('trustDetailedReport', requestParams);
      setShowTable((value) => {
        return {
          ...value,
          totalResults: response?.data?.data?.totalCount || 0,
        };
      });
    } catch (error) {
      setShowTable((value) => {
        return {
          ...value,
          totalResults: 0,
        };
      });
      apiErrorHandler(error);
    }
  };

  const debounceApiCall = useCallback(
    debounce((val, paveValue) => {
      getUpdatedTableData(val, paveValue);
    }, 2000),
    [],
  );

  useEffect(() => {
    setPage(showTable.page);
    setLimit(showTable.limit);
    if (!showTable.isSearchResult) {
      setSearchText('');
    }
  }, [showTable, setLimit, setPage]);

  const onDownloadClick = (response) => {
    successHandler({
      message: {
        message: response.msg,
        title: 'Success',
      },
    });
  };

  useEffect(() => {
    if (showTable.page === page) setSlStartNumber(page * limit - limit);
  }, [showTable, page, limit]);

  const getPageData = () => {
    const startIndex = (page - 1) * limit;
    const _endIndex = startIndex + limit;
    return showTable?.tableData;
  };

  const getHeaders = () => {
    return detailedContractHeader;
  };
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);
  return (
    <>
      <div className="trustaccounts-toolbar">
        {showTable?.tableData ? (
          <Toolbar
            title={title}
            subTitle={subtitle || `As on Date: ${moment().format('MMM DD,YYYY')}`}
            subSubTitle={`No. of Items: ${showTable?.totalResults}`}
            isTableDataPresent={showTable.tableData?.length > 0}
            toolTipText={`This report will show all the reports of ${title}`}
            optionOneLabel="Email Report"
            optionOneIcon="MdOutlineEmail"
            onOptionOneClick={toggleEmailModal}
            optionTwoLabel="Download List"
            optionTwoIcon="FiDownload"
            onOptionTwoClick={toggleDownloadModal}
            searchText={searchText}
            isSearchable
            onSerchInputChange={onSerchInputChange}
            SearchInputPlaceholder="Search with OPI/Contract Number"
          />
        ) : null}
      </div>
      <div className="trustaccounts-section">
        {showTable?.tableData?.length > 0 ? (
          <>
            <div className="trust-listOfContracts">
              <CommonComponentForTableData
                pageData={getPageData()}
                headers={getHeaders()}
                slStartNumber={slStartNumber}
              />
            </div>
            <div className="pagination-araging-report">
              <CommonPagination
                page={page}
                limit={limit}
                totalResults={showTable?.totalResults}
                limitOptions={[5, 10, 15]}
                setPage={(val) => {
                  setPage(val);
                  getUpdatedTableData(searchText, val);
                }}
                setLimit={(val) => {
                  setLimit(val);
                }}
                isPaginationEnabled
              />
            </div>
            <DownloadModal
              isOpen={isDownloadModalOpen}
              toggle={toggleDownloadModal}
              reportType="trustDetailedReport"
              onDownloadClick={onDownloadClick}
              urlParams={{
                startDate: showTable.fromDate,
                endDate: showTable.toDate,
                timezone: moment.tz.guess(),
                spName: showTable?.reportType,
                trustType:
                  showTable?.entity?.toString() !== '' ? showTable?.entity?.toString() : undefined,
                needType:
                  showTable?.agreementTypes?.toString() !== ''
                    ? showTable?.agreementTypes?.toString()
                    : undefined,
                businessUnit:
                  showTable?.businessUnits !== ''
                    ? showTable?.businessUnits?.toString()
                    : undefined,
                search: searchText,
              }}
              willReceiveReportFileInEmail
              module="Trust"
            />
            <EmailModal
              isOpen={isEmailModalOpen}
              toggle={toggleEmailModal}
              reportType="trustDetailedReport"
              apiValues={{
                startDate: showTable.fromDate,
                endDate: showTable.toDate,
                timezone: moment.tz.guess(),
                spName: showTable?.reportType,
                trustType:
                  showTable?.entity?.toString() !== '' ? showTable?.entity?.toString() : undefined,
                needType:
                  showTable?.agreementTypes?.toString() !== ''
                    ? showTable?.agreementTypes?.toString()
                    : undefined,
                businessUnit:
                  showTable?.businessUnits !== ''
                    ? showTable?.businessUnits?.toString()
                    : undefined,
                search: searchText,
              }}
              module="Trust"
              emailOptions={downloadTypesEmail.slice(1)}
              isTrustAccountParams
            />
          </>
        ) : (
          <ItemEmptyTile />
        )}
      </div>
    </>
  );
}
